import React, { useState, useEffect } from 'react';
import { Table, Card, Spin, Tag, Typography, Steps, ConfigProvider, List, Select, Modal, Image, Button, Empty, Grid } from 'antd';
import { CalendarOutlined, PictureOutlined, MenuOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/ru';
import ru_RU from 'antd/locale/ru_RU';
import { instance } from "../../API/API";
import {get_cookie} from "../new_planning/helpers/helper";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Русская локализация
moment.locale('ru');

const theme = {
    "components": {
        "Steps": {
            "controlHeight": 22
        }
    }
}
const hash = get_cookie("hash");

const MaintenanceReports = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.md;

    const currentDate = moment();
    const [reports, setReports] = useState([]);
    const [works, setWorks] = useState([]);
    const [objects, setObjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState(currentDate.year());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.month());
    const [selectedDay, setSelectedDay] = useState(currentDate.date());
    const [mobileView, setMobileView] = useState('list'); // 'list' или 'table'

    // Состояния для модального окна с фотографиями
    const [photoModalVisible, setPhotoModalVisible] = useState(false);
    const [currentPhotos, setCurrentPhotos] = useState([]);
    const [photoLoading, setPhotoLoading] = useState(false);
    const [currentObject, setCurrentObject] = useState(null);
    const [currentReportDate, setCurrentReportDate] = useState(null);

    useEffect(() => {
        loadData();
    }, [selectedYear, selectedMonth, selectedDay]);

    // Автоматически переключать на список на мобильных устройствах
    useEffect(() => {
        if (isMobile) {
            setMobileView('list');
        }
    }, [isMobile]);

    // Генерируем список годов от 2021 до текущего
    const generateYears = () => {
        const currentYear = moment().year();
        const years = [];
        for (let year = 2021; year <= currentYear; year++) {
            years.push(year);
        }
        return years.reverse();
    };

    // Список месяцев с русскими названиями
    const months = [
        { value: 0, label: 'Янв' },
        { value: 1, label: 'Фев' },
        { value: 2, label: 'Мар' },
        { value: 3, label: 'Апр' },
        { value: 4, label: 'Май' },
        { value: 5, label: 'Июн' },
        { value: 6, label: 'Июл' },
        { value: 7, label: 'Авг' },
        { value: 8, label: 'Сен' },
        { value: 9, label: 'Окт' },
        { value: 10, label: 'Ноя' },
        { value: 11, label: 'Дек' }
    ];

    // Генерируем список дней для выбранного месяца и года
    const generateDays = () => {
        if (selectedYear && selectedMonth !== null) {
            const daysInMonth = moment().year(selectedYear).month(selectedMonth).daysInMonth();
            const days = [];
            for (let day = 1; day <= daysInMonth; day++) {
                days.push(day);
            }
            return days;
        }
        return [];
    };

    // Функция для получения timestamp начала и конца периода
    const getPeriodTimestamps = () => {
        if (selectedDay !== null) {
            const startOfDay = moment().year(selectedYear).month(selectedMonth).date(selectedDay).startOf('day');
            const endOfDay = moment().year(selectedYear).month(selectedMonth).date(selectedDay).endOf('day');
            return {
                start: startOfDay.unix(),
                end: endOfDay.unix()
            };
        } else {
            const startOfMonth = moment().year(selectedYear).month(selectedMonth).startOf('month');
            const endOfMonth = moment().year(selectedYear).month(selectedMonth).endOf('month');
            return {
                start: startOfMonth.unix(),
                end: endOfMonth.unix()
            };
        }
    };

    // Функция для получения отчетов
    const fetchReports = async (start, end) => {
        try {
            const response = await instance.get(`reports/get_reports.php?start=${start}&end=${end}&api-key=${hash}`);
            return (response && response.data) ? response.data.reports || [] : [];
        } catch (error) {
            console.error('Ошибка при загрузке отчетов:', error);
            return [];
        }
    };

    // Функция для получения работ
    const fetchWorks = async () => {
        try {
            const response = await instance.get(`reports/get_works_template.php?api-key=${hash}`);
            return (response && response.data) ? response.data.template || [] : [];
        } catch (error) {
            console.error('Ошибка при загрузке работ:', error);
            return [];
        }
    };

    // Функция для получения объектов
    const fetchObjects = async () => {
        try {
            const response = await instance.get(`objects/get_objects.php?avalible=1&showTimes=1&showActual=1&api-key=${hash}`);
            return (response && response.data) ? response.data.objects || [] : [];
        } catch (error) {
            console.error('Ошибка при загрузке объектов:', error);
            return [];
        }
    };

    // Функция для получения фотографий
    const fetchPhotos = async (objectId, start, end) => {
        try {
            setPhotoLoading(true);
            const response = await instance.get(`reports/get_photo_by_time.php?id=${objectId}&start=${start}&end=${end}&api-key=${hash}`);
            const data = response.data;

            if (data.access && data.links) {
                return data.links.map(photo => ({
                    ...photo,
                    fullLink: `https://gdapp.org/data/${photo.link}`
                }));
            }
            return [];
        } catch (error) {
            console.error('Ошибка при загрузке фотографий:', error);
            return [];
        } finally {
            setPhotoLoading(false);
        }
    };

    // Загрузка всех данных
    const loadData = async () => {
        setLoading(true);

        const { start, end } = getPeriodTimestamps();

        try {
            const [reportsData, worksData, objectsData] = await Promise.all([
                fetchReports(start, end),
                fetchWorks(),
                fetchObjects()
            ]);

            setReports(reportsData.sort((a, b) => b.time_download - a.time_download));
            setWorks(worksData);
            setObjects(objectsData);
        } catch (error) {
            console.error('Ошибка при загрузке данных:', error);
        } finally {
            setLoading(false);
        }
    };

    // Обработчик клика по иконке фото
    const handlePhotoClick = async (record) => {
        const objectId = record.object_id;
        const reportDate = moment.unix(record.time_download);

        const startOfDay = reportDate.clone().startOf('day').unix();
        const endOfDay = reportDate.clone().endOf('day').unix();

        setCurrentObject(getObjectName(objectId));
        setCurrentReportDate(reportDate.format('DD.MM.YYYY'));

        const photos = await fetchPhotos(objectId, startOfDay, endOfDay);
        setCurrentPhotos(photos);
        setPhotoModalVisible(true);
    };

    // Обработчики изменения года, месяца и дня
    const handleYearChange = (year) => {
        setSelectedYear(year);
        const daysInNewMonth = moment().year(year).month(selectedMonth).daysInMonth();
        if (selectedDay > daysInNewMonth) {
            setSelectedDay(daysInNewMonth);
        }
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        const daysInNewMonth = moment().year(selectedYear).month(month).daysInMonth();
        if (selectedDay > daysInNewMonth) {
            setSelectedDay(daysInNewMonth);
        }
    };

    const handleDayChange = (day) => {
        setSelectedDay(day);
    };

    // Функция для получения названия объекта по ID
    const getObjectName = (objectId) => {
        if (!Array.isArray(objects)) return `Объект #${objectId}`;
        const object = objects.find(o => o.id === objectId || o.id === objectId.toString());
        return object ? object.name : `Объект #${objectId}`;
    };

    // Функция для получения полного названия объекта по ID
    const getFullObjectName = (objectId) => {
        if (!Array.isArray(objects)) return `Объект #${objectId}`;
        const object = objects.find(o => o.id === objectId || o.id === objectId.toString());
        if (!object) return `Объект #${objectId}`;

        if (isMobile) {
            return (
                <div>
                    <div style={{fontWeight: 'bold', fontSize: '14px'}}>{object.name}</div>
                    {object.long_name && object.name !== object.long_name && (
                        <div style={{fontSize: '12px', color: '#666'}}>
                            {object.long_name.length > 30 ?
                                `${object.long_name.substring(0, 30)}...` : object.long_name}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div>
                <div style={{fontWeight: 'bold'}}>{object.name}</div>
                {object.long_name && object.name !== object.long_name && (
                    <div style={{fontSize: '12px', color: '#666'}}>{object.long_name}</div>
                )}
            </div>
        );
    };

    // Функция для форматирования timestamp
    const formatTimestamp = (timestamp) => {
        return moment.unix(timestamp).format('DD.MM.YYYY HH:mm');
    };

    const formatTimestampShort = (timestamp) => {
        return moment.unix(timestamp).format('HH:mm');
    };

    const getDuration = (date_start, date_end) => {
        const start = new Date(date_start * 1000);
        const end = new Date(date_end * 1000);
        const durationMs = Math.abs(end - start);

        const minutes = Math.floor(durationMs / (1000 * 60));
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;

        return {
            formatted: `${hours}ч ${remainingMinutes}м`,
            hours,
            minutes: remainingMinutes
        };
    };

    // Компонент для отображения выполненных работ
    const WorksWithComments = ({ worksString, commentsString }) => {
        if (!worksString) return '-';

        const workIds = worksString.split(',').filter(id => id.trim() !== '');
        const comments = commentsString && commentsString !== '`````'
            ? commentsString.split('`').filter(comment => comment.trim() !== '')
            : [];

        // Функция для получения названия работы по ID
        const getWorkName = (workId) => {
            // Сравниваем как строки, так как workId из строки, а в works.id - числа
            const work = works.find(w => w.id.toString() === workId.toString());
            return work ? work.name : `Работа #${workId}`;
        };

        if (workIds.includes('0')){
            return (
                <div>
                    <Tag color="red" style={{ margin: 0, fontSize: '11px' }}>
                        Объект был пропущен
                    </Tag>
                    <div style={{fontSize: '12px', marginTop: '10px', color: '#989898'}}>Причина: <span style={{fontWeight: 600, color: '#656565'}}>{comments[0]}</span></div>
                </div>

            )
        }

        if (isMobile) {
            return (
                <div style={{ fontSize: '12px' }}>
                    {workIds.map((workId, index) => {
                        const comment = comments[index] || '';

                        return (
                            <div key={index} style={{ marginBottom: 4 }}>
                                <Tag color="blue" style={{ margin: 0, fontSize: '11px' }}>
                                    {getWorkName(workId)}
                                </Tag>
                                {comment && (
                                    <div style={{
                                        fontSize: '11px',
                                        color: '#666',
                                        marginTop: 2,
                                        fontStyle: 'italic'
                                    }}>
                                        {comment.length > 50 ? `${comment.substring(0, 50)}...` : comment}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        }

        return (
            <List
                size="small"
                dataSource={workIds}
                renderItem={(workId, index) => {
                    const comment = comments[index] || '';

                    return (
                        <List.Item style={{padding: '4px 0'}}>
                            <div style={{ width: '100%' }}>
                                <Tag color="blue" style={{ marginBottom: comment ? 4 : 0 }}>
                                    {getWorkName(workId)}
                                </Tag>
                                {comment && (
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        marginTop: 2,
                                        fontStyle: 'italic',
                                        lineHeight: '1.3'
                                    }}>
                                        {comment}
                                    </div>
                                )}
                            </div>
                        </List.Item>
                    );
                }}
            />
        );
    };

    const TimeDisplay = ({ start, end }) => {
        const duration = getDuration(start, end);

        if (isMobile) {
            return (
                <div style={{ fontSize: '12px' }}>
                    <div>{formatTimestampShort(start)} - {formatTimestampShort(end)}</div>
                    <Tag color="cyan" style={{ fontSize: '11px', marginTop: 2 }}>
                        {duration.formatted}
                    </Tag>
                </div>
            );
        }

        return (
            <ConfigProvider theme={theme}>
                <Steps
                    progressDot
                    current={1}
                    size="small"
                    direction="vertical"
                    items={[
                        { title: formatTimestampShort(start), status: 'wait' },
                        { title: <Tag color="cyan">{duration.formatted}</Tag>, status: 'process' },
                        { title: formatTimestampShort(end), status: 'wait' },
                    ]}
                />
            </ConfigProvider>
        );
    };

    // Колонки для таблицы (десктоп)
    const desktopColumns = [
        {
            title: 'Объект',
            dataIndex: 'object_id',
            key: 'object',
            width: 250,
            render: (objectId) => getFullObjectName(objectId),
        },
        {
            title: 'Время работ',
            dataIndex: 'time_start',
            key: 'time',
            width: 120,
            render: (start, record) => (
                <TimeDisplay start={start} end={record.time_end} />
            ),
        },
        {
            title: 'Время загрузки',
            dataIndex: 'time_download',
            key: 'time_download',
            width: 150,
            render: formatTimestamp,
        },
        {
            title: 'Выполненные работы',
            dataIndex: 'works_id',
            key: 'works',
            render: (worksString, record) => (
                <WorksWithComments
                    worksString={worksString}
                    commentsString={record.comments_id}
                />
            ),
        },
        {
            title: 'Фото',
            key: 'photos',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Button
                    type="text"
                    icon={<PictureOutlined />}
                    onClick={() => handlePhotoClick(record)}
                    style={{ color: '#1890ff' }}
                    title="Посмотреть фотографии"
                />
            ),
        },
    ];

    // Компонент карточки для мобильного вида
    const MobileReportCard = ({ report }) => (
        <Card
            size="small"
            style={{ marginBottom: 12, borderRadius: 8 }}
            bodyStyle={{ padding: '12px' }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                    {getFullObjectName(report.object_id)}
                </div>
                <Button
                    type="text"
                    icon={<PictureOutlined />}
                    onClick={() => handlePhotoClick(report)}
                    style={{ color: '#1890ff', padding: '4px' }}
                    size="small"
                />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, fontSize: '12px' }}>
                <div>
                    <div style={{ color: '#666' }}>Работы:</div>
                    <TimeDisplay start={report.time_start} end={report.time_end} />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <div style={{ color: '#666' }}>Загружено:</div>
                    <div>{formatTimestamp(report.time_download)}</div>
                </div>
            </div>

            <div>
                <div style={{ color: '#666', marginBottom: 4, fontSize: '12px' }}>Выполненные работы:</div>
                <WorksWithComments
                    worksString={report.works_id}
                    commentsString={report.comments_id}
                />
            </div>
        </Card>
    );

    const years = generateYears();
    const days = generateDays();
    const currentMonthName = months.find(m => m.value === selectedMonth)?.label;

    // Формируем текст для отображения периода
    const getPeriodText = () => {
        if (selectedDay !== null) {
            const date = moment().year(selectedYear).month(selectedMonth).date(selectedDay);
            return `Отчеты за ${date.format('DD.MM.YYYY')}`;
        } else {
            return `Отчеты за ${currentMonthName} ${selectedYear}`;
        }
    };

    return (
        <ConfigProvider locale={ru_RU}>
            <Card bodyStyle={{ padding: isMobile ? '12px' : '24px' }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 16,
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? 12 : 0
                }}>
                    <Title level={3} style={{
                        margin: 0,
                        fontSize: isMobile ? '18px' : '24px'
                    }}>
                        Отчеты по обслуживанию
                    </Title>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        justifyContent: isMobile ? 'center' : 'flex-end'
                    }}>
                        <CalendarOutlined />
                        <Select
                            value={selectedYear}
                            onChange={handleYearChange}
                            style={{ width: isMobile ? 80 : 100 }}
                            size={isMobile ? "small" : "middle"}
                            placeholder="Год"
                        >
                            {years.map(year => (
                                <Option key={year} value={year}>{year}</Option>
                            ))}
                        </Select>
                        <Select
                            value={selectedMonth}
                            onChange={handleMonthChange}
                            style={{ width: isMobile ? 90 : 120 }}
                            size={isMobile ? "small" : "middle"}
                            placeholder="Месяц"
                        >
                            {months.map(month => (
                                <Option key={month.value} value={month.value}>{month.label}</Option>
                            ))}
                        </Select>
                        <Select
                            value={selectedDay}
                            onChange={handleDayChange}
                            style={{ width: isMobile ? 70 : 80 }}
                            size={isMobile ? "small" : "middle"}
                            placeholder="День"
                            disabled={!selectedYear || selectedMonth === null}
                        >
                            {days.map(day => (
                                <Option key={day} value={day}>{day}</Option>
                            ))}
                        </Select>

                        {isMobile && (
                            <Button
                                icon={<MenuOutlined />}
                                onClick={() => setMobileView(mobileView === 'list' ? 'table' : 'list')}
                                size="small"
                            >
                                {mobileView === 'list' ? 'Таблица' : 'Список'}
                            </Button>
                        )}
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>Загрузка данных...</div>
                    </div>
                ) : (
                    <>
                        <div style={{ marginBottom: 16 }}>
                            <Text type="secondary" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                                {getPeriodText()}. Всего: {reports.length}
                            </Text>
                        </div>

                        {isMobile && mobileView === 'list' ? (
                            <div>
                                {reports.map(report => (
                                    <MobileReportCard key={report.id} report={report} />
                                ))}
                            </div>
                        ) : (
                            <Table
                                columns={desktopColumns}
                                size="small"
                                dataSource={reports.map(report => ({ ...report, key: report.id }))}
                                pagination={{
                                    pageSize: 10,
                                    showSizeChanger: !isMobile,
                                    showQuickJumper: !isMobile,
                                    showTotal: (total, range) =>
                                        `Показаны ${range[0]}-${range[1]} из ${total} отчетов`,
                                    simple: isMobile
                                }}
                                scroll={{ x: true }}
                                style={{
                                    fontSize: isMobile ? '12px' : '14px'
                                }}
                            />
                        )}
                    </>
                )}

                {/* Модальное окно для фотографий */}
                <Modal
                    title={
                        <div style={{ fontSize: isMobile ? '16px' : '18px' }}>
                            <PictureOutlined style={{ marginRight: 8 }} />
                            Фотографии: {currentObject}
                            {currentReportDate && (
                                <Text type="secondary" style={{ marginLeft: 8, fontSize: isMobile ? '12px' : '14px' }}>
                                    (от {currentReportDate})
                                </Text>
                            )}
                        </div>
                    }
                    open={photoModalVisible}
                    onCancel={() => setPhotoModalVisible(false)}
                    footer={null}
                    width={isMobile ? '100%' : 800}
                    style={{ maxWidth: '100vw' }}
                    bodyStyle={{ padding: isMobile ? '12px' : '24px' }}
                    centered
                >
                    {photoLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>Загрузка фотографий...</div>
                        </div>
                    ) : currentPhotos.length > 0 ? (
                        <Image.PreviewGroup>
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(150px, 1fr))',
                                gap: '8px'
                            }}>
                                {currentPhotos.map((photo, index) => (
                                    <Image
                                        key={index}
                                        src={photo.fullLink}
                                        alt={`Фото ${index + 1}`}
                                        style={{
                                            width: '100%',
                                            height: isMobile ? '120px' : '150px',
                                            objectFit: 'cover',
                                            borderRadius: '4px'
                                        }}
                                        placeholder={
                                            <div style={{
                                                width: '100%',
                                                height: isMobile ? '120px' : '150px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: '#f0f0f0',
                                                borderRadius: '4px'
                                            }}>
                                                <Spin size="small" />
                                            </div>
                                        }
                                    />
                                ))}
                            </div>
                        </Image.PreviewGroup>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Нет фотографий за выбранный период"
                        />
                    )}
                </Modal>
            </Card>
        </ConfigProvider>
    );
};

export default MaintenanceReports;